import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveIcon from '@mui/icons-material/Remove';
import { 
  getDatasets, 
  createDataset, 
  updateDataset, 
  deleteDataset, 
  getLectures, 
  getCourseRuns, 
  getCourses,
  setLectureDataset,
  getDataset,
  getLectureDataset
} from '../services/api';
import { Dataset, Lecture, Course, CourseRun } from '../types';

// Интерфейс для выбранного курса и потока
interface CourseSelection {
  courseId: number;
  courseTitle: string;
  courseRunId: number;
  courseRunTitle: string;
  lectures: Lecture[];
  selectedLectures: number[];
}

// Интерфейс для лекции с дополнительной информацией
interface LectureWithInfo extends Lecture {
  courseTitle: string;
  courseRunTitle: string;
  isSelected: boolean;
}

export const DatasetsPage: React.FC = () => {
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Состояния для диалога создания/редактирования датасета
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [editingDataset, setEditingDataset] = useState<Dataset | null>(null);
  const [datasetTitle, setDatasetTitle] = useState('');
  const [datasetDescription, setDatasetDescription] = useState('');
  
  // Состояния для выбора лекций
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseRuns, setCourseRuns] = useState<CourseRun[]>([]);
  const [lectures, setLectures] = useState<Lecture[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<number | ''>('');
  const [selectedCourseRun, setSelectedCourseRun] = useState<number | ''>('');
  const [loadingLectures, setLoadingLectures] = useState(false);
  
  // Новое состояние для хранения выбранных курсов и потоков с их лекциями
  const [courseSelections, setCourseSelections] = useState<CourseSelection[]>([]);
  
  // Новое состояние для хранения всех лекций с информацией о курсе и потоке
  const [allLectures, setAllLectures] = useState<LectureWithInfo[]>([]);
  
  // Состояние для отслеживания загрузки данных при редактировании
  const [loadingEditData, setLoadingEditData] = useState(false);
  
  // Состояние для диалога выбора лекций при редактировании
  const [showLectureSelectionDialog, setShowLectureSelectionDialog] = useState(false);
  const [selectedLectureIds, setSelectedLectureIds] = useState<number[]>([]);
  
  // Загрузка датасетов при монтировании компонента
  useEffect(() => {
    fetchDatasets();
    fetchCourses();
  }, []);
  
  // Загрузка потоков курса при изменении выбранного курса
  useEffect(() => {
    if (selectedCourse) {
      fetchCourseRuns(selectedCourse as number);
    } else {
      setCourseRuns([]);
      setSelectedCourseRun('');
    }
  }, [selectedCourse]);
  
  // Загрузка лекций при изменении выбранного потока курса
  useEffect(() => {
    if (selectedCourseRun) {
      fetchLectures(selectedCourseRun as number);
    } else {
      setLectures([]);
    }
  }, [selectedCourseRun]);
  
  // Функция для загрузки датасетов
  const fetchDatasets = async () => {
    try {
      setLoading(true);
      const data = await getDatasets();
      setDatasets(data);
      setError(null);
    } catch (err) {
      console.error('Ошибка при загрузке датасетов:', err);
      setError('Не удалось загрузить датасеты');
    } finally {
      setLoading(false);
    }
  };
  
  // Функция для загрузки курсов
  const fetchCourses = async () => {
    try {
      const data = await getCourses();
      setCourses(data);
    } catch (err) {
      console.error('Ошибка при загрузке курсов:', err);
    }
  };
  
  // Функция для загрузки потоков курса
  const fetchCourseRuns = async (courseId: number) => {
    try {
      const data = await getCourseRuns(courseId);
      setCourseRuns(data);
    } catch (err) {
      console.error('Ошибка при загрузке потоков курса:', err);
    }
  };
  
  // Функция для загрузки лекций
  const fetchLectures = async (courseRunId: number) => {
    try {
      setLoadingLectures(true);
      const data = await getLectures(courseRunId);
      setLectures(data);
    } catch (err) {
      console.error('Ошибка при загрузке лекций:', err);
    } finally {
      setLoadingLectures(false);
    }
  };
  
  // Функция для открытия диалога создания датасета
  const handleOpenCreateDialog = () => {
    setDialogTitle('Создать новый датасет');
    setEditingDataset(null);
    setDatasetTitle('');
    setDatasetDescription('');
    setSelectedCourse('');
    setSelectedCourseRun('');
    setLectures([]);
    setCourseSelections([]);
    setAllLectures([]);
    setOpenDialog(true);
  };
  
  // Функция для открытия диалога редактирования датасета
  const handleOpenEditDialog = async (dataset: Dataset) => {
    setDialogTitle('Редактировать датасет');
    setEditingDataset(dataset);
    setDatasetTitle(dataset.title);
    setDatasetDescription(dataset.description || '');
    setSelectedCourse('');
    setSelectedCourseRun('');
    setLectures([]);
    setCourseSelections([]);
    setAllLectures([]);
    setLoadingEditData(true);
    
    try {      
      // Загружаем все курсы и потоки
      const coursesData = await getCourses();
      setCourses(coursesData);
      
      // Создаем массив для хранения информации о лекциях
      const lecturesWithInfo: LectureWithInfo[] = [];
      
      // Для каждого курса загружаем потоки и лекции
      for (const course of coursesData) {
        const courseRuns = await getCourseRuns(course.id);
        
        for (const courseRun of courseRuns) {
          const lectures = await getLectures(courseRun.id);
          
          // Проверяем каждую лекцию на принадлежность к датасету
          for (const lecture of lectures) {
            try {
              // Получаем информацию о датасете для этой лекции
              const lectureDataset = await getLectureDataset(lecture.id);
              
              // Если лекция привязана к текущему датасету
              if (lectureDataset && lectureDataset.id === dataset.id) {
                lecturesWithInfo.push({
                  ...lecture,
                  courseTitle: course.title,
                  courseRunTitle: courseRun.title,
                  isSelected: true
                });
              }
            } catch (err) {
              console.error(`Ошибка при проверке датасета для лекции ${lecture.id}:`, err);
            }
          }
        }
      }
      
      // Сохраняем информацию о лекциях в состоянии
      setAllLectures(lecturesWithInfo);
      
    } catch (err) {
      console.error('Ошибка при загрузке данных для редактирования датасета:', err);
      setError('Не удалось загрузить данные для редактирования датасета');
    } finally {
      setLoadingEditData(false);
    }
    
    setOpenDialog(true);
  };
  
  // Функция для закрытия диалога
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  // Функция для создания или обновления датасета
  const handleSaveDataset = async () => {
    if (!datasetTitle.trim()) {
      setError('Название датасета не может быть пустым');
      return;
    }
    
    try {
      let savedDataset;
      
      if (editingDataset) {
        // Обновление существующего датасета
        savedDataset = await updateDataset(
          editingDataset.id, 
          datasetTitle, 
          datasetDescription || undefined
        );

        // Обновляем привязки лекций к датасету
        if (allLectures.length > 0) {
          for (const lecture of allLectures) {
            // Если лекция выбрана, но не привязана к датасету
            if (lecture.isSelected && lecture.datasetId !== editingDataset.id) {
              try {
                const result = await setLectureDataset(lecture.id, editingDataset.id);
              } catch (error) {
                console.error('Ошибка при привязке лекции к датасету:', error);
              }
            }
            // Если лекция не выбрана, но привязана к датасету
            else if (!lecture.isSelected && lecture.datasetId === editingDataset.id) {
              try {
                const result = await setLectureDataset(lecture.id, null);
              } catch (error) {
                console.error('Ошибка при отвязке лекции от датасета:', error);
              }
            }
          }
        }
      } else {
        savedDataset = await createDataset(
          datasetTitle, 
          datasetDescription || undefined
        );
        
        // Добавляем задержку перед привязкой лекций
        await new Promise(resolve => setTimeout(resolve, 500));
        
        for (const selection of courseSelections) {
          for (const lectureId of selection.selectedLectures) {
            try {
              await new Promise(resolve => setTimeout(resolve, 100));
              
              const result = await setLectureDataset(lectureId, savedDataset.id);
            } catch (error) {
              console.error('Ошибка при привязке лекции к датасету:', error);
            }
          }
        }
      }
      
      // Обновление списка датасетов
      await fetchDatasets();
      
      // Закрытие диалога
      handleCloseDialog();
    } catch (err) {
      console.error('Ошибка при сохранении датасета:', err);
      setError('Не удалось сохранить датасет');
    }
  };
  
  // Функция для удаления датасета
  const handleDeleteDataset = async (datasetId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот датасет?')) {
      return;
    }
    
    try {
      await deleteDataset(datasetId);
      await fetchDatasets();
    } catch (err) {
      console.error('Ошибка при удалении датасета:', err);
      setError('Не удалось удалить датасет');
    }
  };
  
  // Функция для открытия датасета
  const handleOpenDataset = (datasetId: number) => {
    navigate(`/datasets/${datasetId}`);
  };
  
  // Функция для добавления текущего выбора курса и потока
  const handleAddCourseSelection = () => {
    if (!selectedCourse || !selectedCourseRun) {
      return;
    }
    
    const courseTitle = courses.find(c => c.id === selectedCourse)?.title || '';
    const courseRunTitle = courseRuns.find(r => r.id === selectedCourseRun)?.title || '';
    
    // Проверяем, не добавлен ли уже этот поток курса
    const exists = courseSelections.some(
      selection => selection.courseRunId === selectedCourseRun
    );
    
    if (exists) {
      alert('Этот поток курса уже добавлен');
      return;
    }
    
    // Добавляем новый выбор курса и потока
    setCourseSelections([
      ...courseSelections,
      {
        courseId: selectedCourse as number,
        courseTitle,
        courseRunId: selectedCourseRun as number,
        courseRunTitle,
        lectures: [...lectures],
        selectedLectures: []
      }
    ]);
    
    // Сбрасываем текущий выбор
    setSelectedCourse('');
    setSelectedCourseRun('');
    setLectures([]);
  };
  
  // Функция для удаления выбранного курса и потока
  const handleRemoveCourseSelection = (index: number) => {
    setCourseSelections(prev => prev.filter((_, i) => i !== index));
  };
  
  // Функция для выбора/отмены выбора лекции в конкретном потоке курса
  const handleToggleLecture = (selectionIndex: number, lectureId: number) => {
    setCourseSelections(prev => {
      const newSelections = [...prev];
      const selection = newSelections[selectionIndex];
      
      if (selection.selectedLectures.includes(lectureId)) {
        selection.selectedLectures = selection.selectedLectures.filter(id => id !== lectureId);
      } else {
        selection.selectedLectures = [...selection.selectedLectures, lectureId];
      }
      
      return newSelections;
    });
  };
  
  // Функция для добавления новой лекции в датасет при редактировании
  const handleAddLectureToDataset = () => {
    // Открываем диалог выбора лекций без проверки на выбранный курс и поток
    setShowLectureSelectionDialog(true);
  };
  
  // Функция для удаления лекции из датасета при редактировании
  const handleRemoveLectureFromDataset = async (lectureId: number) => {
    if (!editingDataset) return;
    
    if (window.confirm('Вы уверены, что хотите удалить эту лекцию из датасета?')) {
      try {
        await setLectureDataset(lectureId, null);
        
        // Удаляем лекцию из локального состояния
        setAllLectures(prev => prev.filter(lecture => lecture.id !== lectureId));
      } catch (error) {
        console.error('Ошибка при отвязке лекции от датасета:', error);
        setError('Не удалось удалить лекцию из датасета');
      }
    }
  };
  
  // Функция для выбора лекций из текущего потока для добавления в датасет
  const handleSelectLecturesForDataset = async (selectedLectureIds: number[]) => {
    if (!selectedLectureIds.length) {
      return;
    }
    
    const courseTitle = courses.find(c => c.id === selectedCourse)?.title || '';
    const courseRunTitle = courseRuns.find(r => r.id === selectedCourseRun)?.title || '';
    
    // Добавляем выбранные лекции в список
    const newLectures = lectures
      .filter(lecture => selectedLectureIds.includes(lecture.id))
      .map(lecture => ({
        ...lecture,
        courseTitle,
        courseRunTitle,
        isSelected: true
      }));
    
    // Добавляем только те лекции, которых еще нет в списке
    const existingLectureIds = allLectures.map(l => l.id);
    const lecturesToAdd = newLectures.filter(l => !existingLectureIds.includes(l.id));
    
    // Если редактируем существующий датасет, отправляем запросы на сервер
    if (editingDataset) {
      try {
        for (const lecture of lecturesToAdd) {
          await setLectureDataset(lecture.id, editingDataset.id);
        }
      } catch (error) {
        console.error('Ошибка при привязке лекций к датасету:', error);
        setError('Не удалось привязать некоторые лекции к датасету');
      }
    }
    
    setAllLectures(prev => [...prev, ...lecturesToAdd]);
    setShowLectureSelectionDialog(false);
    
    // Сбрасываем выбор
    setSelectedCourse('');
    setSelectedCourseRun('');
    setLectures([]);
    setSelectedLectureIds([]);
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Датасеты
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Создать датасет
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : datasets.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            Датасеты не найдены. Создайте новый датасет, нажав на кнопку выше.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {datasets.map(dataset => (
            <Grid item xs={12} sm={6} md={4} key={dataset.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {dataset.title}
                  </Typography>
                  {dataset.description && (
                    <Typography variant="body2" color="textSecondary">
                      {dataset.description}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Создан: {new Date(dataset.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Комментариев: {dataset.comments && dataset.comments.length > 0 && 
                        ('count' in dataset.comments[0] ? dataset.comments[0].count : 
                        dataset.comments.length)}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    color="primary"
                    onClick={() => handleOpenDataset(dataset.id)}
                  >
                    Открыть
                  </Button>
                  <Box sx={{ flexGrow: 1 }} />
                  <Tooltip title="Редактировать">
                    <IconButton 
                      size="small"
                      onClick={() => handleOpenEditDialog(dataset)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Удалить">
                    <IconButton 
                      size="small"
                      onClick={() => handleDeleteDataset(dataset.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Диалог создания/редактирования датасета */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Название датасета"
              fullWidth
              value={datasetTitle}
              onChange={(e) => setDatasetTitle(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              label="Описание (необязательно)"
              fullWidth
              value={datasetDescription}
              onChange={(e) => setDatasetDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
            
            {/* Показываем разные интерфейсы для создания и редактирования */}
            {editingDataset ? (
              // Интерфейс для редактирования датасета
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">
                    Лекции в датасете
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddLectureToDataset}
                  >
                    Добавить лекцию
                  </Button>
                </Box>
                
                {loadingEditData ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Курс</TableCell>
                          <TableCell>Поток</TableCell>
                          <TableCell>Лекция</TableCell>
                          <TableCell align="right">Действия</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allLectures.map(lecture => (
                          <TableRow 
                            key={lecture.id}
                            hover
                          >
                            <TableCell>{lecture.courseTitle}</TableCell>
                            <TableCell>{lecture.courseRunTitle}</TableCell>
                            <TableCell>{`${lecture.order}. ${lecture.title}`}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveLectureFromDataset(lecture.id)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        {allLectures.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              Нет привязанных лекций. Добавьте лекции, нажав на кнопку "Добавить лекцию".
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                
                {/* Диалог выбора лекций для добавления в датасет */}
                <Dialog
                  open={showLectureSelectionDialog}
                  onClose={() => setShowLectureSelectionDialog(false)}
                  maxWidth="md"
                  fullWidth
                >
                  <DialogTitle>Выберите лекции для добавления в датасет</DialogTitle>
                  <DialogContent>
                    <Box sx={{ pt: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth margin="normal">
                            <InputLabel>Курс</InputLabel>
                            <Select
                              value={selectedCourse}
                              onChange={(e) => {
                                setSelectedCourse(e.target.value as number);
                                setSelectedLectureIds([]);
                              }}
                              label="Курс"
                            >
                              <MenuItem value="">
                                <em>Не выбрано</em>
                              </MenuItem>
                              {courses.map(course => (
                                <MenuItem key={course.id} value={course.id}>
                                  {course.title}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth margin="normal" disabled={!selectedCourse}>
                            <InputLabel>Поток курса</InputLabel>
                            <Select
                              value={selectedCourseRun}
                              onChange={(e) => {
                                setSelectedCourseRun(e.target.value as number);
                                setSelectedLectureIds([]);
                              }}
                              label="Поток курса"
                            >
                              <MenuItem value="">
                                <em>Не выбрано</em>
                              </MenuItem>
                              {courseRuns.map(run => (
                                <MenuItem key={run.id} value={run.id}>
                                  {run.title}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      
                      {loadingLectures ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : selectedCourseRun && lectures.length > 0 ? (
                        <Box sx={{ mt: 3, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Выберите лекции для добавления:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {lectures
                              // Фильтруем лекции, которые уже добавлены в датасет
                              .filter(lecture => !allLectures.some(l => l.id === lecture.id))
                              .map(lecture => (
                                <Chip
                                  key={lecture.id}
                                  label={`${lecture.order}. ${lecture.title}`}
                                  onClick={() => {
                                    setSelectedLectureIds(prev => 
                                      prev.includes(lecture.id)
                                        ? prev.filter(id => id !== lecture.id)
                                        : [...prev, lecture.id]
                                    );
                                  }}
                                  color={selectedLectureIds.includes(lecture.id) ? "primary" : "default"}
                                  variant={selectedLectureIds.includes(lecture.id) ? "filled" : "outlined"}
                                  size="small"
                                />
                              ))}
                            {lectures.filter(lecture => !allLectures.some(l => l.id === lecture.id)).length === 0 && (
                              <Typography variant="body2" color="textSecondary">
                                Все лекции из этого потока уже добавлены в датасет
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : selectedCourseRun ? (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                          Лекции не найдены для выбранного потока курса
                        </Typography>
                      ) : null}
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setShowLectureSelectionDialog(false)}>Отмена</Button>
                    <Button 
                      onClick={() => handleSelectLecturesForDataset(selectedLectureIds)}
                      variant="contained" 
                      color="primary"
                      disabled={selectedLectureIds.length === 0}
                    >
                      Добавить выбранные лекции
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            ) : (
              // Интерфейс для создания нового датасета
              <>
                <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
                  Привязать лекции к датасету
                </Typography>
                
                {/* Выбор курса и потока */}
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Курс</InputLabel>
                      <Select
                        value={selectedCourse}
                        onChange={(e) => setSelectedCourse(e.target.value as number)}
                        label="Курс"
                      >
                        <MenuItem value="">
                          <em>Не выбрано</em>
                        </MenuItem>
                        {courses.map(course => (
                          <MenuItem key={course.id} value={course.id}>
                            {course.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth margin="normal" disabled={!selectedCourse}>
                      <InputLabel>Поток курса</InputLabel>
                      <Select
                        value={selectedCourseRun}
                        onChange={(e) => setSelectedCourseRun(e.target.value as number)}
                        label="Поток курса"
                      >
                        <MenuItem value="">
                          <em>Не выбрано</em>
                        </MenuItem>
                        {courseRuns.map(run => (
                          <MenuItem key={run.id} value={run.id}>
                            {run.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleAddCourseSelection}
                      disabled={!selectedCourse || !selectedCourseRun || loadingLectures || lectures.length === 0}
                      sx={{ mt: 3 }}
                      fullWidth
                    >
                      Добавить ещё
                    </Button>
                  </Grid>
                </Grid>
                
                {/* Отображение лекций для текущего выбранного потока */}
                {loadingLectures ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : selectedCourseRun && lectures.length > 0 ? (
                  <Box sx={{ mt: 3, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Выберите лекции из текущего потока:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {lectures.map(lecture => {
                        // Проверяем, выбрана ли уже эта лекция в текущем потоке
                        const existingSelection = courseSelections.find(
                          selection => selection.courseRunId === selectedCourseRun
                        );
                        const isSelected = existingSelection?.selectedLectures.includes(lecture.id) || false;
                        
                        return (
                          <Chip
                            key={lecture.id}
                            label={`${lecture.order}. ${lecture.title}`}
                            onClick={() => {
                              // Добавляем лекцию в текущий выбор
                              const courseTitle = courses.find(c => c.id === selectedCourse)?.title || '';
                              const courseRunTitle = courseRuns.find(r => r.id === selectedCourseRun)?.title || '';
                              
                              // Проверяем, существует ли уже такой выбор
                              const existingIndex = courseSelections.findIndex(
                                selection => selection.courseRunId === selectedCourseRun
                              );
                              
                              if (existingIndex >= 0) {
                                // Обновляем существующий выбор
                                setCourseSelections(prev => {
                                  const newSelections = [...prev];
                                  const selection = newSelections[existingIndex];
                                  
                                  if (selection.selectedLectures.includes(lecture.id)) {
                                    selection.selectedLectures = selection.selectedLectures.filter(id => id !== lecture.id);
                                  } else {
                                    selection.selectedLectures = [...selection.selectedLectures, lecture.id];
                                  }
                                  
                                  return newSelections;
                                });
                              } else {
                                // Создаем новый выбор
                                setCourseSelections(prev => [
                                  ...prev,
                                  {
                                    courseId: selectedCourse as number,
                                    courseTitle,
                                    courseRunId: selectedCourseRun as number,
                                    courseRunTitle,
                                    lectures: [...lectures],
                                    selectedLectures: [lecture.id]
                                  }
                                ]);
                              }
                            }}
                            color={isSelected ? "primary" : "default"}
                            variant={isSelected ? "filled" : "outlined"}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  </Box>
                ) : selectedCourseRun ? (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Лекции не найдены для выбранного потока курса
                  </Typography>
                ) : null}
                
                {/* Список выбранных курсов и потоков */}
                {courseSelections.length > 0 && (
                  <Box sx={{ mt: 3, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Выбранные потоки курсов:
                    </Typography>
                    <List>
                      {courseSelections.map((selection, index) => (
                        <React.Fragment key={`${selection.courseId}-${selection.courseRunId}`}>
                          <ListItem
                            secondaryAction={
                              <IconButton 
                                edge="end" 
                                onClick={() => handleRemoveCourseSelection(index)}
                                size="small"
                              >
                                <RemoveIcon />
                              </IconButton>
                            }
                          >
                            <ListItemText 
                              primary={`${selection.courseTitle} - ${selection.courseRunTitle}`}
                              secondary={`Выбрано лекций: ${selection.selectedLectures.length} из ${selection.lectures.length}`}
                            />
                          </ListItem>
                          <Box sx={{ pl: 2, pr: 2, pb: 2 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {selection.lectures.map(lecture => (
                                <Chip
                                  key={lecture.id}
                                  label={`${lecture.order}. ${lecture.title}`}
                                  onClick={() => handleToggleLecture(index, lecture.id)}
                                  color={selection.selectedLectures.includes(lecture.id) ? "primary" : "default"}
                                  variant={selection.selectedLectures.includes(lecture.id) ? "filled" : "outlined"}
                                  size="small"
                                />
                              ))}
                            </Box>
                          </Box>
                          {index < courseSelections.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button 
            onClick={handleSaveDataset} 
            variant="contained" 
            color="primary"
            disabled={!datasetTitle.trim()}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}; 